import { toast } from 'sonner';
import { useCallback } from 'react';
import { useMutation } from '@apollo/client/react';

import { SoftwareOnboardingMain } from 'src/components/SoftwareOnboarding';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { AssetVisibility } from 'src/containers/SoftwareEscrow/Technical';
import {
  PartyNotificationSettings,
  ActivityNotifications,
  DisruptedAccessNotifications,
} from 'src/components/Escrow/Notifications';
import {
  ActivityNotificationEnum,
  DisruptionNotificationEnum,
  DisruptionNotificationGracePeriodEnum,
  EscrowPartyTypeEnum,
  IEscrowUpdateBeneficiaryNotificationsParams,
} from 'src/graphql/schema';
import { EscrowUpdateBeneficiaryNotificationsDocument } from 'src/graphql/actions/escrow/escrowUpdateBeneficiaryNotifications.mutation';
import { SoftwareEscrowUpdateDocument } from 'src/graphql/actions/softwareEscrow/softwareEscrowUpdate.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import { getEscrowActivityNotificationOptions } from 'src/shared/entities/escrow-activity-notification-options';
import { disruptedNotificationsOptionsRich, gracePeriodOptions } from 'src/shared/entities/escrow-notification-options';

const { activityNotificationOptionsRich } = getEscrowActivityNotificationOptions('software');

const partyContentMap = {
  [EscrowPartyTypeEnum.Beneficiary]: {
    title: 'Notification settings for the beneficiary',
    description: 'Manage the information and notifications you’ll receive.',
    noteText: 'You can edit this later under Settings → Technical Information.',
  },
  [EscrowPartyTypeEnum.Depositor]: {
    title: 'Asset visibility for beneficiaries',
    description:
      'Control whether the beneficiary can view details about deposited assets, including file names, deposit dates, and connection status.',
    noteText: 'You can edit this later under Settings → Technical Information.',
  },
};

const BeneficiaryNotificationSettings = () => {
  const {
    softwareEscrow: {
      id: softwareEscrowId,
      beneficiaryActivityNotification,
      beneficiaryDisruptionNotification,
      beneficiaryDisruptionNotificationGracePeriod,
    },
  } = useSoftwareEscrow();
  const [updateBeneficiaryNotifications] = useMutation(EscrowUpdateBeneficiaryNotificationsDocument);

  const onBeneficiaryNotificationsChange = useCallback(
    async (escrowParams: IEscrowUpdateBeneficiaryNotificationsParams) => {
      try {
        const { data } = await updateBeneficiaryNotifications({
          variables: { escrowId: softwareEscrowId, escrowParams },
        });

        if (data?.escrowUpdateBeneficiaryNotifications?.success) {
          toast.success('Saved!');
        } else {
          throw new ClientError(data?.escrowUpdateBeneficiaryNotifications?.errors?.[0]);
        }
      } catch (e: unknown) {
        getApiError(e, toast.error);
      }
    },
    [softwareEscrowId, updateBeneficiaryNotifications],
  );

  const onActivityNotificationChange = useCallback(
    (beneficiaryActivityNotification: ActivityNotificationEnum) => {
      if (!beneficiaryActivityNotification) return;

      onBeneficiaryNotificationsChange({ beneficiaryActivityNotification });
    },
    [onBeneficiaryNotificationsChange],
  );

  const onDisruptionNotificationChange = useCallback(
    (beneficiaryDisruptionNotification: DisruptionNotificationEnum) => {
      if (!beneficiaryDisruptionNotification) return;

      onBeneficiaryNotificationsChange({ beneficiaryDisruptionNotification });
    },
    [onBeneficiaryNotificationsChange],
  );

  const onGracePeriodChange = useCallback(
    (beneficiaryDisruptionNotificationGracePeriod: DisruptionNotificationGracePeriodEnum) => {
      if (!beneficiaryDisruptionNotificationGracePeriod) return;

      onBeneficiaryNotificationsChange({ beneficiaryDisruptionNotificationGracePeriod });
    },
    [onBeneficiaryNotificationsChange],
  );

  return (
    <PartyNotificationSettings
      className="md:grid-cols-1"
      activityNotificationNode={
        <ActivityNotifications
          description={`What type of activity notifications would you like to receive?\nThese may include details like file names and deposit timestamps.`}
          value={beneficiaryActivityNotification}
          options={activityNotificationOptionsRich}
          onChange={onActivityNotificationChange}
        />
      }
      disruptedAccessNotificationNode={
        <DisruptedAccessNotifications
          description="Choose whether to alert members if an asset becomes inaccessible or can no longer be deposited."
          values={{
            disruptionNotification: beneficiaryDisruptionNotification,
            disruptionNotificationGracePeriod: beneficiaryDisruptionNotificationGracePeriod ?? null,
          }}
          options={{
            disruptionNotificationOptions: disruptedNotificationsOptionsRich,
            gracePeriodOptions: gracePeriodOptions,
          }}
          onDisruptionNotificationChange={onDisruptionNotificationChange}
          onDisruptionNotificationGracePeriodChange={onGracePeriodChange}
        />
      }
    />
  );
};

const EnvironmentVisibilityPage = () => {
  const [updateDepositSettings] = useMutation(SoftwareEscrowUpdateDocument);
  const {
    softwareEscrow: { id: softwareEscrowId, beneficiarySharedInformation, currentCompanyRole },
  } = useSoftwareEscrow();

  const onAssetVisibilityChange = async (beneficiarySharedInformation: boolean | null) => {
    if (beneficiarySharedInformation === null) return;

    try {
      const { data } = await updateDepositSettings({
        variables: {
          softwareEscrowId,
          softwareEscrowParams: {
            beneficiarySharedInformation,
          },
        },
      });

      if (data?.softwareEscrowUpdate?.success) {
        toast.success('Saved!');
      } else {
        throw new ClientError(data?.softwareEscrowUpdate?.errors?.[0]);
      }
    } catch (e: unknown) {
      getApiError(e, toast.error);
    }
  };

  const { title, description, noteText } = partyContentMap[currentCompanyRole!];

  const showBeneficiarySettings =
    currentCompanyRole === EscrowPartyTypeEnum.Beneficiary && beneficiarySharedInformation;

  return (
    <SoftwareOnboardingMain
      subtitle="Technical information:"
      title={title}
      description={description}
      noteText={noteText}
    >
      <div className="w-[580px] max-w-full mx-auto overflow-hidden space-y-10">
        <AssetVisibility
          onAssetVisibilityChange={onAssetVisibilityChange}
          assetVisibility={beneficiarySharedInformation}
          party={currentCompanyRole!}
        />
        {showBeneficiarySettings && <BeneficiaryNotificationSettings />}
      </div>
    </SoftwareOnboardingMain>
  );
};

export default EnvironmentVisibilityPage;
